'use client';

import { useCallback, useMemo } from 'react';
import type Stream from '@personly/libs-stream';

const streams = new Map<string, Promise<Stream>>();

const loadStream = (
  scope: string,
  url: string,
  onError: (err: string) => void
) => {
  if (!streams.has(url)) {
    const options = { path: `/${scope}`, transports: ['websocket', 'polling'] };

    const stream = import('@personly/libs-stream').then(
      ({ default: Stream }) => new Stream(url, options, onError)
    );

    streams.set(url, stream);
  }

  return streams.get(url)!;
};

const useStream = (
  accessToken: string | null | undefined,
  onError: (e: string) => void,
  onConnectError: (e: Error, cb?: (accessToken: string) => void) => void,
  scope: string,
  url: string
) => {
  const subscribe = useCallback(
    async <T>(channel: string, event: string, cb: (response: T) => void) => {
      const stream = await loadStream(scope, url, onError);
      if (accessToken) stream.setAccessToken(accessToken);
      return stream.subscribe(channel, event, cb, (err) =>
        accessToken
          ? onConnectError(err, stream.setAccessToken.bind(stream))
          : onConnectError(err)
      );
    },
    [scope, url, onError, onConnectError, accessToken]
  );

  const unsubscribe = useCallback(
    async (handle: string) => {
      const stream = await loadStream(scope, url, onError);
      return stream.unsubscribe(handle);
    },
    [scope, url, onError]
  );

  const emit = useCallback(
    async (handle: string, event: string) => {
      const stream = await loadStream(scope, url, onError);
      stream.emit(handle, event);
    },
    [scope, url, onError]
  );

  const value = useMemo(
    () => ({ subscribe, unsubscribe, emit }),
    [subscribe, unsubscribe, emit]
  );

  return value;
};

export default useStream;
